import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireAdminOrModerator } from "./auth-roles.server";

const pingInputSchema = z.object({
  carId: z.string().uuid(),
  status: z.enum(["active", "draft", "sold", "reserved"]),
  brand: z.string().optional(),
  model: z.string().optional(),
});

/**
 * Notify search engines + warm caches when a car's status flips.
 *
 * Why this matters:
 *   Google/Bing crawl on their own schedule, but they explicitly support a
 *   GET-ping to /ping?sitemap=... that bumps the URL into their crawl queue.
 *   This shaves hours-to-days off the discovery latency for sold/reserved
 *   updates. We also "touch" the public feeds (HEAD) so Cloudflare's edge
 *   layer revalidates ahead of the next merchant fetch.
 *
 *   Meta's catalog feed has no public ping endpoint — their crawler honours
 *   the Cache-Control + Last-Modified headers, which we set to s-maxage=300
 *   so a status flip propagates within 5 minutes worst-case.
 *
 * Failure mode:
 *   All outbound calls run with Promise.allSettled and a 4s timeout — a
 *   slow/unreachable Google/Meta endpoint must NOT block the admin's
 *   status-update mutation. We log+swallow per-target errors and return
 *   a per-target summary so the admin UI can show what fired.
 */
export const pingSearchEnginesForCarStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => pingInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdminOrModerator(context.userId);

    const domain = process.env.PUBLIC_DOMAIN || "autoxchange.dk";
    const sitemapUrl = `https://${domain}/sitemap.xml`;
    const facebookFeedUrl = `https://${domain}/feeds/facebook`;
    const googleFeedUrl = `https://${domain}/feeds/google`;

    const fetchWithTimeout = async (url: string, init: RequestInit = {}, ms = 4000) => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), ms);
      try {
        const res = await fetch(url, { ...init, signal: ctrl.signal });
        return { ok: res.ok, status: res.status };
      } finally {
        clearTimeout(t);
      }
    };

    const targets = [
      {
        name: "google_sitemap_ping",
        url: `https://www.google.com/ping?sitemap=${encodeURIComponent(sitemapUrl)}`,
      },
      {
        name: "bing_sitemap_ping",
        url: `https://www.bing.com/ping?sitemap=${encodeURIComponent(sitemapUrl)}`,
      },
      {
        name: "feed_facebook_warm",
        url: facebookFeedUrl,
        init: { method: "HEAD" } as RequestInit,
      },
      {
        name: "feed_google_warm",
        url: googleFeedUrl,
        init: { method: "HEAD" } as RequestInit,
      },
    ];

    const settled = await Promise.allSettled(
      targets.map((t) => fetchWithTimeout(t.url, t.init)),
    );

    const results = targets.map((t, i) => {
      const r = settled[i];
      if (r.status === "fulfilled") {
        return { name: t.name, ok: r.value.ok, httpStatus: r.value.status };
      }
      return {
        name: t.name,
        ok: false,
        httpStatus: 0,
        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
      };
    });

    return {
      carId: data.carId,
      status: data.status,
      pingedAt: new Date().toISOString(),
      results,
    };
  });
